import React, { useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Box, CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import ScrollToTop from "./components/ScrollToTop";
import PageTransition from "./components/PageTransition";
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import AppointmentPage from "./pages/AppointmentPage";
import { openWhatsApp } from "./utils/whatsapp";
import { useSeo, useJsonLd, localBusinessSchema, breadcrumbSchema } from "./utils/seo";

/* ── Two Sense — Monochrome Palette ──
   Ink:        #000000   (pure black)
   Paper:      #ffffff   (pure white)
   Soft:       #fafafa   (off-white surface)
   Mist:       #f0f0f0   (subtle gray surface)
   Mid:        #666666   (secondary text / quiet accent)
*/
const theme = createTheme({
    palette: {
        primary: {
            main: "#000000",
            light: "#1a1a1a",
            dark: "#000000",
        },
        secondary: {
            main: "#000000",
            light: "#333333",
            dark: "#000000",
        },
        background: {
            default: "#ffffff",
            paper: "#ffffff",
        },
        text: {
            primary: "#000000",
            secondary: "#666666",
        },
    },
    typography: {
        fontFamily: '"Inter", "Helvetica Neue", "Segoe UI", sans-serif',
        h1: {
            fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
            fontWeight: 400,
            letterSpacing: "-0.5px",
            fontSize: "4.2rem",
            lineHeight: 1.05,
            "@media (max-width: 1024px)": { fontSize: "3.4rem" },
            "@media (max-width: 768px)": { fontSize: "2.6rem" },
            "@media (max-width: 480px)": { fontSize: "2.1rem" },
        },
        h2: {
            fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
            fontWeight: 400,
            letterSpacing: "-0.3px",
            fontSize: "3rem",
            lineHeight: 1.15,
            "@media (max-width: 1024px)": { fontSize: "2.4rem" },
            "@media (max-width: 768px)": { fontSize: "1.9rem" },
            "@media (max-width: 480px)": { fontSize: "1.6rem" },
        },
        h3: {
            fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
            fontWeight: 500,
            fontSize: "1.9rem",
            lineHeight: 1.25,
            "@media (max-width: 768px)": { fontSize: "1.5rem" },
        },
        h4: {
            fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
            fontWeight: 500,
            fontSize: "1.5rem",
            lineHeight: 1.3,
        },
        body1: {
            fontFamily: '"Inter", sans-serif',
            fontSize: "1rem",
            lineHeight: 1.75,
            fontWeight: 400,
        },
        body2: {
            fontFamily: '"Inter", sans-serif',
            fontSize: "0.92rem",
            lineHeight: 1.7,
        },
        button: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
        },
    },
});

/* Route-aware head management. Lives inside <Router> so it can read the current
   pathname, and is rendered as a component (not a hook in App) because
   useLocation is only valid below the Router. Emits the tags Google reads on
   client navigation; the same values are pre-baked into static HTML for
   crawlers that don't run JS — see scripts/generate-static-seo.js. */
function SeoHead() {
    const { pathname } = useLocation();
    useSeo(pathname);
    const business = useMemo(() => localBusinessSchema(), []);
    const breadcrumbs = useMemo(() => breadcrumbSchema(pathname), [pathname]);
    useJsonLd("ld-business", business);
    useJsonLd("ld-breadcrumbs", breadcrumbs);
    return null;
}

function App() {
    const handleBooking = (serviceName) => openWhatsApp(serviceName);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <SeoHead />
                <ScrollToTop />
                <Box sx={{ width: "100%", overflowX: "hidden", display: "flex", flexDirection: "column", minHeight: "100vh", background: "#ffffff" }}>
                    <Header />

                    <Box sx={{ flex: 1 }}>
                        <PageTransition>
                            <Routes>
                                <Route path="/" element={<HomePage onBookingClick={handleBooking} />} />
                                <Route path="/services" element={<ServicesPage />} />
                                <Route path="/appointment" element={<AppointmentPage />} />
                            </Routes>
                        </PageTransition>
                    </Box>

                    <WhatsAppButton />
                    <Footer />
                </Box>
            </Router>
        </ThemeProvider>
    );
}

export default App;
